import { CellModel, ColumnSource, RequestFilters, RowSource } from "@omegagrid/grid";
import { ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import rp, { GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { Domain } from "@reporting/rp-types/domain";

/**
 * Custom domains of the installation (P_DOMAIN in the super db, SA only).
 * Only the columns below travel through the grid - the mapper keeps the
 * cert/acme blobs of an edited row untouched.
 */
export class DomainGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			data: 'controls',
			t: 'a',
			e: false,
			w: 5 + 20 * 2,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red,
			}, {
				action: 'requestCert',
				title: msg('Request certificate'),
				icon: 'file-certificate',
				color: ColorTypes.Accent,
			}],
			filter: false
		}, {
			data: 'id_domain',
			header: '#',
			t: 'n',
			e: false,
			w: 60,
		}, {
			data: 'domain_name',
			header: msg('Domain'),
			t: 't',
			e: true,
			w: 250,
		}, {
			data: 'primary_domain',
			header: msg('Primary domain'),
			t: 't',
			e: true,
			w: 250,
		}, {
			data: 'cert_authority',
			header: msg('Certificate authority'),
			t: 't',
			e: false,
			w: 150,
		}, {
			data: 'cert_issued',
			header: msg('Issued'),
			t: 'dt',
			e: false,
			w: 145,
		}, {
			data: 'cert_expiration',
			header: msg('Expiration'),
			t: 'dt',
			e: false,
			w: 145,
		}, {
			data: 'acme_status',
			header: msg('Status'),
			t: 't',
			e: false,
			w: 100,
		}, {
			data: 'idz',
			t: 'n',
			hidden: true,
		}];
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const domains = await rp.ms('domain').get<Domain[]>('domains', this.createRequestParams(filters, {
			fields: ['idz', 'id_domain', 'domain_name', 'primary_domain', 'cert_authority', 'cert_issued', 'cert_expiration', 'acme_status'],
			order: ['domain_name'],
		}));
		return domains.map(data => ({ data }));
	}

	async save(rowData: RowData[]) {
		const data = rowData.map(row => Object.assign(
			{ _state: row.state, _index: row.index }, row.data
		));
		await rp.ms('domain').post('domains', data);
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			rowNumbers: true,
			columnSorting: true,
			filters: true,
			pageSize: null,
			refreshAfterSave: true,
			saveMode: 'edited_rows',
			toolbar: {
				new: {text: msg('Add domain')},
			},
			rowMenu: {
				addRows: true,
				deleteRows: true,
			},
		});
	}

	async onAction(action: string, cell: CellModel): Promise<void> {
		if (action !== 'requestCert') return;

		const domain = cell?.row.data as Domain;
		if (!domain?.id_domain) {
			rp.host.bucket.showAlert({type: 'warning', content: msg('Save the domain first')});
			return;
		}

		// an issued certificate gets renewed, a missing one requested
		const renew = !!domain.cert_issued;
		const command = await rp.host.openMenu({
			content: 'yes_no',
			text: `${renew ? msg('Renew certificate') : msg('Request certificate')}: ${domain.domain_name}?`,
		});
		if (command?.id != 'Y') return;

		try {
			await rp.ms('domain').post(`acme/${renew ? 'renew' : 'request'}/${domain.id_domain}`, {});
			rp.host.bucket.showAlert({type: 'success', content: msg('Certificate issued')});
		} catch (error) {
			rp.host.bucket.showAlert({type: 'danger', title: msg('Certificate request failed'), content: String(error)});
		}

		this.editor.refresh();
	}

}
